// app/composables/squadSimulator.ts

import type { Player } from "~~/src/player/domain/Player";
import { useWebContext } from "~/composables/webContext";
import { useImages } from "~/composables/images";

interface UseSquadSimulator {
  MIN_PLAYERS: number;
  MAX_PLAYERS: number;
  createPlaceholderPlayer: () => Player;
  addPlaceholderToMaxPlayers: (players: Player[]) => Player[];
  isMinPlayers: (players: Player[]) => boolean;
  isMaxPlayers: (players: Player[]) => boolean;
  isPlayerMinLimitReached: (players: Player[]) => boolean;
  isPlayerMaxLimitReached: (players: Player[]) => boolean;
  updateTeamBudget: (budget: number, amount: number) => number;
  filterPlayersWithMarketValues: (squad: Player[]) => Player[];
}

export const useSquadSimulator = (): UseSquadSimulator => {
  const { webContextGenders } = useWebContext();
  const { playerPlaceholderPath } = useImages();

  const placeholderIdCounter = ref<number>(1);

  const MIN_PLAYERS = 8;
  const MAX_PLAYERS = 12;

  function createPlaceholderPlayer(): Player {
    return {
      id: -placeholderIdCounter.value++, // Unique negative IDs for placeholders
      shortName: undefined,
      role: undefined,
      gender: webContextGenders[0], //male or female
      countryId: 0,
      jersey: "",
      birthDate: "1900-01-01T00:00:00Z",
      height: null,
      image: {
        url: playerPlaceholderPath,
      },
      metaInformation: null,
      category: "draft",
      marketValue: 0,
    };
  }

  function addPlaceholderToMaxPlayers(players: Player[]): Player[] {
    const currentCount = players.length;

    if (currentCount < MAX_PLAYERS) {
      // Add placeholders until there are MAX players
      const placeholdersToAdd = MAX_PLAYERS - currentCount;
      const placeholders = Array(placeholdersToAdd)
        .fill(null)
        .map(() => createPlaceholderPlayer());
      players.push(...placeholders);
    }
    return players;
  }

  //No less than Min or min
  function isMinPlayers(players: Player[]) {
    const actualPlayers = players.filter(player => player.id > 0); // Exclude placeholders
    return actualPlayers.length <= MIN_PLAYERS;
  }
  //No more than max or max
  function isMaxPlayers(players: Player[]) {
    const actualPlayers = players.filter(player => player.id > 0); // Exclude placeholders
    return actualPlayers.length >= MAX_PLAYERS;
  }
  //No less than Min
  function isPlayerMinLimitReached(players: Player[]) {
    return players.filter(p => p.id > 0).length < MIN_PLAYERS;
  }
  //No more than max
  function isPlayerMaxLimitReached(players: Player[]) {
    return players.filter(p => p.id > 0).length > MAX_PLAYERS;
  }

  function updateTeamBudget(budget: number, amount: number): number {
    return budget + amount;
  }

  function filterPlayersWithMarketValues(squad: Player[]): Player[] {
    return squad
      .filter(p => p.category === "draft" || !p.category)
      .filter(p => p.marketValue && p.marketValue > 0);
  }

  return {
    MIN_PLAYERS,
    MAX_PLAYERS,
    createPlaceholderPlayer,
    isMinPlayers,
    isMaxPlayers,
    addPlaceholderToMaxPlayers,
    updateTeamBudget,
    isPlayerMaxLimitReached,
    isPlayerMinLimitReached,
    filterPlayersWithMarketValues,
  };
};
